import React, { Context, Key, useContext, useEffect, useState } from 'react';

export type FiltersMap = {
  [x: string]: string | unknown; // `unknown` to allow any type of filter value to be set
};

interface FilterContextValue<K extends FiltersMap> {
  setFilters: (filters: Partial<K>) => void;
  filters: Partial<K>;
}

export const createFilterContext = function createContext<
  K extends FiltersMap,
>() {
  return React.createContext({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    setFilters: (_filters: Partial<K>) => {},
    filters: {},
  });
};

export function FilterProvider<K extends FiltersMap>(props: {
  context: React.Context<FilterContextValue<K>>;
  children: React.ReactNode | React.ReactNodeArray;
}) {
  const [filters, setFilters] = useState<Partial<K>>({});

  return (
    <props.context.Provider value={{ setFilters, filters }}>
      {props.children}
    </props.context.Provider>
  );
}

export function useFilters<K extends FiltersMap>(
  context: Context<FilterContextValue<Partial<K>>>,
  defaultFilters?: Partial<K>,
) {
  const { filters, setFilters: setFiltersOnContext } = useContext(context);

  const setFilters = (
    newFilters: Partial<K>,
    options?: { clearExisting?: boolean },
  ) => {
    const updatedFilters = options?.clearExisting
      ? newFilters
      : { ...filters, ...newFilters };

    setFiltersOnContext(updatedFilters);
  };

  // `value: unknown` to allow any type of filter value to be set
  // TOOD: Remove unknown
  const setFilter = (filterKey: keyof K, value: unknown): void => {
    const clone = { ...filters, [filterKey]: value };
    setFilters(clone);
  };

  const clearFilter = (key: keyof K) => {
    const toClear: Partial<K> = { ...filters };
    delete toClear[key];
    setFilters(toClear);
  };

  /**
   * Sets default filter values on the context.
   * Note: This does not clear any filter values
   * of keys that are not included in defaultFilters.
   */
  useEffect(() => {
    if (!defaultFilters) return;
    const filterOverridesSet = Object.keys(defaultFilters).filter(
      (key) => !!(filters as Record<string, K[Key]>)[key],
    ).length;
    if (!filterOverridesSet) {
      setFilters(defaultFilters);
    }
  }, [defaultFilters]);

  return { filters, setFilters, setFilter, clearFilter };
}
